import { isTempClipId } from '@src/services/WriteQueueService';

import type { Story } from '@src/types.ts/story';

/** Minimal shape needed to test story membership. */
type ClipLike = { id?: string | null };

/**
 * Whether `clip` is part of `story` — i.e. its id appears in
 * `story.otherVideos` (a main clip) or within any main clip's
 * `associatedVideos` (a non-original aspect-ratio variant).
 *
 * This is the invariant that makes it safe for the clip-preview stack to read
 * the live `videoCreator.story` (resource fetch scoping, save writebacks):
 * when it holds, the loaded story IS the clip's story. The previewed clip only
 * appears in `otherVideos` once its story has finished loading, so this is
 * false during the cross-navigation window where the global story still holds
 * the previous page's story.
 *
 * Temp (optimistic) clips are treated as belonging: their id is local and may
 * lag the `otherVideos` entry across the temp→real id swap
 * (`replaceOptimisticVideoId` updates the story entry + `currentVideo`, but the
 * preview object keeps the temp id briefly), and their resources are generated
 * locally rather than fetched by storyId. See `useClipResourceLoader`.
 */
export function clipBelongsToStory(
  clip: ClipLike | null | undefined,
  story: Pick<Story, 'otherVideos'> | null | undefined,
): boolean {
  const clipId = clip?.id;
  if (!clipId) return false;
  if (isTempClipId(clipId)) return true;
  return !!story?.otherVideos?.some(
    v => v.id === clipId || v.associatedVideos?.some(av => av.id === clipId),
  );
}
